import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { WorkViewerInfo } from '../../shared/component/headless/input/InputBody';
import HeadlessBtn from '../../shared/component/headless/button/HeadlessBtn';
import { BlackFitBtn } from '../../shared/component/headless/button/BtnBody';
import MoleculeInputDivForModal from '../../shared/component/molecule/MoleculeInputDivForModal';
import { useModalStore } from '../../shared/store/portal/modalStore';
import { getBtnName } from '../../shared/constants/btnNames';
import { useSetting } from '../../shared/hooks/useApi/useSetting';
import { getConfig } from '../../shared/env/envManager';
import { convertS3UrlToCloudFrontUrl } from '../../shared/aws/awsHelper';
import { AlertPosition, AlertType, ModalType, TextAlignment } from '../../shared/enum/EnumRepository';
import { SEO_IMAGE_MAX_SIZE, SEO_TITLE_MAX_LENGTH } from '../../shared/constants/limits';
import { paperTextureSmall } from '../../shared/component/paperTexture';
import { slideInFromRightWeighted } from '../../shared/component/Animation';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import deleteIcon from '../../asset/icon/delete.png';
import ReplaceImageButtonForModal from '../../shared/component/ReplaceImageButtonForModal';
import { useImage } from '../../shared/hooks/useApi/useImage';
import { compressImageFile } from '../../shared/util/imageUtils';
import { useStandardAlertStore } from '../../shared/store/portal/alertStore';
import { getAlertMessage } from '../../shared/constants/alertMessages';
import { getSettingsMessage } from '../../shared/constants/pageMessages';

interface ModalProps {
  setIsUserInput: (isUserInput: boolean) => void;
}

const DEFAULT_OG_IMAGE_URL = '/og-image.png';
const PREVIEW_SUB_TITLE_MAX_LENGTH = 40;
const SEO_IMAGE_ACCEPT = '.jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp';
const SEO_IMAGE_ACCEPTED_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
const SEO_IMAGE_ACCEPTED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp'];

const ChangeShareModal: React.FC<ModalProps> = ({ setIsUserInput }) => {
  const { standardModal, isClosing, clearModal, setStandardModal } = useModalStore();
  const { setting, updateSeo } = useSetting();
  const { uploadSeoImage } = useImage();
  const { enqueueAlert } = useStandardAlertStore();
  const { appDomain } = getConfig();
  const [subTitle, setSubTitle] = useState(setting?.seo.subTitle ?? '');
  const [imageUrl, setImageUrl] = useState(setting?.seo.uploadFile.originUrl ?? '');
  const [imageSize, setImageSize] = useState({
    width: setting?.seo.uploadFile.width ?? 0,
    height: setting?.seo.uploadFile.height ?? 0,
  });

  useEffect(() => {
    if (!setting) {
      setIsUserInput(false);
      return;
    }

    setIsUserInput(
      subTitle !== setting.seo.subTitle ||
      imageUrl !== setting.seo.uploadFile.originUrl
    );
  }, [imageUrl, setIsUserInput, setting, subTitle]);

  if (standardModal == null || setting == null) return null;

  const previewTitle = [setting.seo.name, setting.seo.nameEn]
    .map((value) => value.trim())
    .filter(Boolean)
    .join(' ') || setting.username;
  const previewSubTitle = subTitle.length > PREVIEW_SUB_TITLE_MAX_LENGTH
    ? `${subTitle.slice(0, PREVIEW_SUB_TITLE_MAX_LENGTH)}...`
    : subTitle;
  const previewImageUrl = imageUrl.trim() === '' ? DEFAULT_OG_IMAGE_URL : convertS3UrlToCloudFrontUrl(imageUrl);

  const handleImageChange = (originUrl: string, width: number, height: number) => {
    setImageUrl(originUrl);
    setImageSize({ width, height });
  };

  const isSeoImageFileAccepted = (file: File) => (
    SEO_IMAGE_ACCEPTED_TYPES.has(file.type) ||
    SEO_IMAGE_ACCEPTED_EXTENSIONS.some((extension) => file.name.toLowerCase().endsWith(extension))
  );

  const handleInvalidImageFile = () => {
    enqueueAlert({
      type: AlertType.ALERT,
      position: AlertPosition.TOP,
      content: getAlertMessage('UNSUPPORTED_IMAGE_FORMAT'),
    });
  };

  const handleChange = async () => {
    const baseRequest = {
      subTitle,
      name: setting.seo.name,
      nameEn: setting.seo.nameEn,
      description: setting.seo.description,
      descriptionEn: setting.seo.descriptionEn ?? '',
      updateUploadFileReq: {
        uploadFileId: setting.seo.uploadFile.id,
        originUrl: imageUrl,
        allowDownload: setting.seo.uploadFile.allowDownload,
        width: imageSize.width,
        height: imageSize.height,
      },
    };

    const finalRequest = imageUrl.startsWith('data:')
      ? await uploadSeoImage(baseRequest)
      : baseRequest;
    if (!finalRequest) return;

    await updateSeo(finalRequest);
  };

  const handleEditTitle = () => {
    setStandardModal({
      modalType: ModalType.CHANGE_SEARCH_ENGINE,
      title: getSettingsMessage('SETTINGS_SEARCH'),
      value: '',
      handleChange: () => {},
    });
  };

  return (
    <LeftModalFrame onClick={(e) => e.stopPropagation()} $isClosing={isClosing}>
      <ModalBody>
        <HeaderRow>
          <Title>{standardModal.title}</Title>
          <UniversalIconBtn
            size="small"
            border={false}
            isTransparent
            hoverOpacity
            backgroundImg={deleteIcon}
            onClick={() => clearModal(700)}
          />
        </HeaderRow>
        <PreviewCardWrapper>
          <PreviewCard>
            <PreviewImageWrapper>
              <PreviewImage src={previewImageUrl} alt="" />
              <PreviewImageBtnWrapper>
                <ReplaceImageButtonForModal
                  setImageUrl={handleImageChange}
                  accept={SEO_IMAGE_ACCEPT}
                  isFileAccepted={isSeoImageFileAccepted}
                  onInvalidFile={handleInvalidImageFile}
                  prepareImage={(file) => compressImageFile(file, { maxBytes: SEO_IMAGE_MAX_SIZE })}
                />
              </PreviewImageBtnWrapper>
            </PreviewImageWrapper>
            <PreviewBody>
              <PreviewTextWrapper>
                <PreviewTitle>{previewTitle}</PreviewTitle>
                <PreviewDescription>{previewSubTitle}</PreviewDescription>
              </PreviewTextWrapper>
              <PreviewLink>{appDomain}</PreviewLink>
            </PreviewBody>
          </PreviewCard>
        </PreviewCardWrapper>
        <OptionContainer>
          <InfoRow>
            <InfoLabel>Title</InfoLabel>
            <ReadOnlyValue>{previewTitle}</ReadOnlyValue>
            <EditInSearchButton type="button" onClick={handleEditTitle}>
              Edit in Search
            </EditInSearchButton>
          </InfoRow>
          <InfoRow>
            <InfoLabel>SubTitle</InfoLabel>
            <MoleculeInputDivForModal
              value={subTitle}
              placeholder="SubTitle"
              handleChange={(e) => setSubTitle(e.target.value)}
              inputStyle={WorkViewerInfo}
              alignment={TextAlignment.LEFT}
              maxLength={SEO_TITLE_MAX_LENGTH}
            />
          </InfoRow>
        </OptionContainer>
        <BtnContainer>
          <HeadlessBtn value={getBtnName('CHANGE')} handleClick={handleChange} StyledBtn={BlackFitBtn} />
        </BtnContainer>
      </ModalBody>
    </LeftModalFrame>
  );
};

const LeftModalFrame = styled.div<{ $isClosing: boolean }>`
  position: absolute;
  right: clamp(20px, 2vw, 30px);
  bottom: calc(8vh);
  width: 26vw;
  min-width: 400px;
  height: clamp(50vh, fit-content, 80vh);
  padding: 20px;
  box-sizing: border-box;
  opacity: ${({ $isClosing }) => ($isClosing ? 0 : 1)};
  transform: ${({ $isClosing }) => ($isClosing ? 'translateX(100%)' : 'translateX(0)')};
  transition: all 0.6s ease-out;
  ${paperTextureSmall}
  ${slideInFromRightWeighted}
`;

const ModalBody = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: clamp(10px, 4vh, 24px);
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 1.5vh;
  flex-shrink: 0;
`;

const Title = styled.div`
  min-width: 0;
  ${({ theme }) => theme.typography.Body_02_1};
`;

const PreviewCardWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  margin-bottom: 14px;
`;

const PreviewCard = styled.div`
  width: 80%;
  min-width: 280px;
  aspect-ratio: 4 / 3;
  overflow: hidden;
  border-radius: 12px;
  box-shadow: 2px 4px 8px 0 rgba(0, 0, 0, 0.05);
`;

const PreviewImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 60%;
  overflow: hidden;
  cursor: pointer;
  &:hover > div {
    opacity: 1;
  }
`;

const PreviewImage = styled.img`
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
`;

const PreviewImageBtnWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
  opacity: 0;
  transition: opacity 0.5s ease-in-out;
`;

const PreviewBody = styled.div`
  padding: 8px 12px 16px 12px;
  height: 40%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const PreviewTextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const PreviewTitle = styled.div`
  color: ${({ theme }) => theme.colors.color_Gray_02};
  ${({ theme }) => theme.typography.Body_03_1};
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const PreviewDescription = styled.div`
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
`;

const PreviewLink = styled.div`
  color: #4aa3ff;
  text-decoration: underline;
  ${({ theme }) => theme.typography.Body_04};
`;

const OptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
`;

const InfoRow = styled.div`
  min-height: 18px;
  margin-bottom: 1px;
  display: flex;
  gap: 8px;
  ${({ theme }) => theme.typography.Body_04};
`;

const InfoLabel = styled.span`
  min-width: 70px;
  padding: 4px 0;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  font-weight: 500;
`;

const ReadOnlyValue = styled.span`
  min-width: 0;
  flex: 1;
  padding: 4px 0;
  overflow: hidden;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const EditInSearchButton = styled.button`
  flex-shrink: 0;
  padding: 4px 0;
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  text-decoration: underline;
  cursor: pointer;
  ${({ theme }) => theme.typography.Body_04};
`;

const BtnContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin: 10px 0px;
`;

export default ChangeShareModal;
